import React from 'react'
import {Link as ReactRouterLink, useParams} from 'react-router-dom'
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Text,
} from '../../../../utils/chakraMonomorphic'
import {RouterLink as Link} from '../../../../utils/chakraRouterLink'

import {useFaceOffRun} from '../../../../hooks/useFaceOffRun'
import {api} from '../../../../services/api'
import {
  FaceOffChange,
  FaceOffFlip,
  FaceOffRunDetail,
  FaceOffVerdictValue,
} from '../../../../services/coreApi/faceoff'
import {useRequiredAuth} from '../../../../stores/AuthStore'
import copy from './copy.json'
import {FACE_OFF_PATH, VERDICT_KEYS, fillCopy} from './constants'
import {FaceOffFaceCard, cardFace} from './FaceOffFaceCard'
import {FaceOffInspector, TruthMap} from './FaceOffInspector'
import {useFaceOffGoldenVersion} from '../../../../hooks/useFaceOffGolden'
import {useFaceOffRuns} from '../../../../hooks/useFaceOffRuns'

interface CardState {
  verdict: FaceOffVerdictValue | null
  note: string
  saving: boolean
  saveError: boolean
}

const changeKey = (change: FaceOffChange): string => {
  const face = cardFace(change)
  return `${change.kind}:${face?.photoId}:${face?.personId}:${face?.path}`
}

const initialCardStates = (flips: FaceOffFlip[]): CardState[] =>
  flips.map((flip) => ({
    verdict: flip.verdict?.verdict ?? null,
    note: flip.verdict?.note ?? '',
    saving: false,
    saveError: false,
  }))

const BackLink: React.FC = () => (
  <Box mb={4}>
    <Link
      as={ReactRouterLink}
      to={FACE_OFF_PATH}
      color='var(--fo-link)'
      textDecoration='underline'>
      {copy.backToRuns}
    </Link>
  </Box>
)

/**
 * One run's diff: flips, then shifts. A reviewer saves a flip's verdict with
 * A, W or U and moves between flips with Tab and Shift+Tab.
 */
export const FaceOffDiff: React.FC = () => {
  const {runId} = useParams<{runId: string}>()
  const {client} = useRequiredAuth()
  const clientId = Number(client.ID)
  const {status, data, notFound} = useFaceOffRun(runId)

  const [cards, setCards] = React.useState<CardState[]>([])
  const cardRefs = React.useRef<(HTMLDivElement | null)[]>([])

  React.useEffect(() => {
    setCards(data ? initialCardStates(data.flips) : [])
  }, [data])

  const updateCard = (index: number, patch: Partial<CardState>): void => {
    setCards((prev) =>
      prev.map((card, i) => (i === index ? {...card, ...patch} : card)),
    )
  }

  const saveVerdict = async (
    flip: FaceOffFlip,
    index: number,
    verdict: FaceOffVerdictValue,
  ): Promise<void> => {
    const face = cardFace(flip)
    if (!face) return
    updateCard(index, {saving: true, saveError: false})
    const response = await api.faceoff.saveFaceOffVerdict(runId, {
      face: {
        path: face.path,
        photoId: face.photoId,
        personId: face.personId,
        bbox: face.bbox,
      },
      verdict,
      note: cards[index]?.note ?? '',
    })
    if (response.result === 'success') {
      updateCard(index, {saving: false, verdict})
    } else {
      // Keep the earlier verdict; the card shows the failure.
      updateCard(index, {saving: false, saveError: true})
    }
  }

  const handleKeyDown =
    (flip: FaceOffFlip, index: number) =>
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.target !== event.currentTarget) return
      if (event.key === 'Tab') {
        const next = cardRefs.current[event.shiftKey ? index - 1 : index + 1]
        if (next) {
          event.preventDefault()
          next.focus()
        }
        return
      }
      if (event.ctrlKey || event.metaKey || event.altKey) return
      const verdict = VERDICT_KEYS[event.key.toLowerCase()]
      if (verdict) {
        event.preventDefault()
        saveVerdict(flip, index, verdict)
      }
    }

  if (status === 'pending' || status === 'idle') {
    return (
      <Flex alignItems='center' gap={3} data-testid='face-off-diff-loading'>
        <Spinner size='sm' />
        <Text fontSize='sm'>{copy.diff.loading}</Text>
      </Flex>
    )
  }

  if (status === 'rejected' || !data) {
    return (
      <>
        <BackLink />
        <Text
          color={notFound ? 'var(--fo-muted)' : 'var(--fo-red)'}
          fontSize='sm'
          data-testid={
            notFound ? 'face-off-diff-not-found' : 'face-off-diff-error'
          }>
          {notFound ? copy.diff.notFound : copy.diff.error}
        </Text>
      </>
    )
  }

  const {run} = data

  return (
    <Box data-testid='face-off-diff'>
      <BackLink />
      <Heading size='md' mb={1}>
        {fillCopy(copy.diff.heading, {runId: run.runId})}
      </Heading>
      <Text fontSize='sm' mb={4}>
        {fillCopy(copy.diff.summary, {
          goldenVersion: run.goldenVersion,
          passed: run.passed,
          total: run.total,
          passRate: `${(run.passRate * 100).toFixed(1)}%`,
          band: run.band,
        })}
      </Text>

      {data.rebaseline ? (
        <Text mb={6} data-testid='face-off-rebaseline'>
          {fillCopy(copy.diff.rebaseline, {
            baselineFrom: data.baselineFrom ?? '',
          })}
        </Text>
      ) : (
        <Box mb={6}>
          <Heading size='sm' mb={2}>
            {copy.diff.flips}
          </Heading>
          {data.flips.length === 0 ? (
            <Text fontSize='sm'>{copy.diff.noFlips}</Text>
          ) : (
            <>
              <Text fontSize='sm' color='var(--fo-muted)' mb={3}>
                {copy.diff.keysHelp}
              </Text>
              {data.flips.map((flip, index) => (
                <FaceOffFaceCard
                  key={changeKey(flip)}
                  ref={(el: HTMLDivElement | null): void => {
                    cardRefs.current[index] = el
                  }}
                  change={flip}
                  clientId={clientId}
                  testId='face-off-flip'
                  verdict={cards[index]?.verdict ?? null}
                  saving={cards[index]?.saving}
                  saveError={cards[index]?.saveError}
                  note={cards[index]?.note}
                  onNoteChange={(note): void => updateCard(index, {note})}
                  onVerdict={(verdict): void => {
                    saveVerdict(flip, index, verdict)
                  }}
                  onKeyDown={handleKeyDown(flip, index)}
                />
              ))}
            </>
          )}
        </Box>
      )}

      <Heading size='sm' mb={2}>
        {copy.diff.shifts}
      </Heading>
      {data.shifts.length === 0 ? (
        <Text fontSize='sm'>{copy.diff.noShifts}</Text>
      ) : (
        data.shifts.map((shift) => (
          <FaceOffFaceCard
            key={changeKey(shift)}
            change={shift}
            clientId={clientId}
            testId='face-off-shift'
          />
        ))
      )}

      <InspectorWithTruth detail={data} clientId={clientId} />
    </Box>
  )
}

/**
 * The inspector, with each face's truth and every photo from the run's golden
 * version, and the run list for its compare picker.
 */
const InspectorWithTruth: React.FC<{
  detail: FaceOffRunDetail
  clientId: number
}> = ({detail, clientId}) => {
  const golden = useFaceOffGoldenVersion(detail.run.goldenVersion)
  const {runs} = useFaceOffRuns()
  const photos = React.useMemo(
    () =>
      golden.data?.photos.map((p) => ({photoId: p.photoId, url: p.url})) ?? [],
    [golden.data],
  )
  const truth = React.useMemo<TruthMap>(() => {
    const out: TruthMap = {}
    golden.data?.photos.forEach((p) =>
      p.cases.forEach((c) => {
        // Face Off's Truth column shows the case kind: positive, nonmatch.
        out[`${p.photoId}:${c.personId}`] = c.kind
      }),
    )
    return out
  }, [golden.data])
  return (
    <FaceOffInspector
      detail={detail}
      clientId={clientId}
      truth={truth}
      photos={photos}
      runs={runs}
    />
  )
}
